import { JHA_THEMES, setJhaWcTheme } from '@banno/jha-wc/src/styles/theme-provider';
import { DiContainer, InjectionToken } from '@jkhy/frontend-utils/di';
import { JhHttpClientCached } from '@jkhy/frontend-utils/http';
import { DynamicComponentRegistry } from '@jkhy/frontend-utils/lit/dynamic-component-registry';
import { AlarmClockService, AlarmConfig, parseTime } from '@treasury/alarm-clock';
import { AnalyticsEvent, AnalyticsService, NullAnalyticsService } from '@treasury/core/analytics';
import { ConfigurationService } from '@treasury/core/config';
import { TmHttpClient } from '@treasury/core/http';
import { LoggingService } from '@treasury/core/logging';
import { NavigationService } from '@treasury/core/navigation';
import '@treasury/domain-components/feature-flags/feature-flag-button';
import { openIdentityDialog } from '@treasury/domain-components/identity-verification-dialog';
import { AccountService, ChannelAuthenticationService } from '@treasury/domain/channel/services';
import { CompanyAccountService } from '@treasury/domain/company/company-account.service';
import { DashboardService } from '@treasury/domain/dashboard';
import { EntitlementsService } from '@treasury/domain/entitlements';
import { NavigationsService } from '@treasury/domain/navigation';
import { RemoteDepositCaptureService } from '@treasury/domain/remote-deposit-capture';
import { AuthFlowEvent, AuthenticationService } from '@treasury/domain/services/authentication';
import { DeviceService } from '@treasury/domain/services/device/device.service';
import {
    Feature,
    FeatureFlagService,
    NullFeatureFlagService,
} from '@treasury/domain/services/feature-flags';
import {
    FeatureVisibilityService,
    PwaVisibilityFeatureConditionMap,
} from '@treasury/domain/services/feature-visibility';
import {
    LogoutManager,
    OpenIdleDialogToken,
    OpenTimeAccessDialogToken,
} from '@treasury/domain/services/logout';
import { ChannelStatusService } from '@treasury/domain/services/status/channel-status.service';
import { StatusService } from '@treasury/domain/services/status/status.service';
import { OpenIdentityDialogToken, TmHttpClientMfa } from '@treasury/domain/shared';
import '@treasury/omega/css/index.css';
import { openIdleDialog } from '@treasury/omega/domain-components/idle-logout-dialog';
import { openTimeAccessDialog } from '@treasury/omega/domain-components/time-access-logout-dialog';
import { TmFooterService } from '@treasury/presentation';
import { OfflineDialogService } from '@treasury/presentation/components/offline';
import { TmDialogService } from '@treasury/presentation/services/dialog';
import { ModernDialogService } from '@treasury/presentation/services/dialog/modern-dialog.service';
import {
    DrawerConfirmationPromptToken,
    SideDrawerPromptFn,
} from '@treasury/presentation/services/side-drawer';
import { ThemeService } from '@treasury/presentation/services/theme';
import colors from '@treasury/presentation/styles/colors.css';
import baseStyles from '@treasury/presentation/styles/styles.css';
import { SessionStorageService, WindowService } from '@treasury/utils/services';
import camelCase from 'camelcase';
import { AppComponent } from './components/app-component';
import dialogThemes from './components/dialog-themes.css';
import './components/jhd-data-field';
import { registerWorker } from './helpers';
import { ApplicationAreaService } from './services/application-area';
import { PwaNavigationService } from './services/navigation';

const [, , institutionId] = window.location.pathname.split('/');

document.adoptedStyleSheets = [...document.adoptedStyleSheets, baseStyles, colors, dialogThemes];

const themeColorMetaTag = document.createElement('meta');
themeColorMetaTag.name = 'theme-color';
themeColorMetaTag.content = getComputedStyle(document.documentElement).getPropertyValue(
    '--brand-primary'
);
document.getElementsByTagName('head')[0].appendChild(themeColorMetaTag);

(async function main(institutionId: string, version: string) {
    setJhaWcTheme(JHA_THEMES.DESIGN_SYSTEM);
    const worker = await registerWorker(institutionId, version);
    await bootstrap(worker);
})(institutionId, window.treasury.version);

function configureBranding(institutionId: string) {
    const head = document.querySelector('HEAD');
    const link = document.createElement('link');

    if (!head) {
        throw new Error('Could not configure branding. No head or link element.');
    }

    link.rel = 'stylesheet';
    link.type = 'text/css';
    link.href = `/branding/${institutionId}/style.css`;
    head.appendChild(link);
    const faviconLink = document.createElement('link');
    faviconLink.rel = 'icon';
    faviconLink.href = `/branding/${institutionId}/favicon.ico`;
    head.appendChild(faviconLink);
}

function buildAlarmConfigs(
    processingCutoffs: NonNullable<
        Awaited<ReturnType<CompanyAccountService['getCutoffTimes']>>['processingCutoffs']
    >
): AlarmConfig[] {
    const alarms: AlarmConfig[] = [];

    processingCutoffs.forEach(cutoff => {
        const { startTime, cutoffTime, productType } = cutoff;

        if (startTime) {
            alarms.push({ key: camelCase(`${productType} start`), time: startTime });
        }

        if (cutoffTime) {
            const cutoffKeyName = camelCase(`${productType} cutoff`);
            alarms.push({ key: cutoffKeyName, time: cutoffTime });

            const cutoffTimeObject = parseTime(cutoffTime);
            cutoffTimeObject.subtractMinutes(30);
            alarms.push({ key: `${cutoffKeyName}-30Minutes`, time: cutoffTimeObject });
        }
    });

    return alarms;
}

let coBrowseInitialized = false;
let cachedIsUisEnabled = false;

async function bootstrap(worker?: ServiceWorkerRegistration) {
    const di = new DiContainer();
    const openDialogForSideDrawer: SideDrawerPromptFn = async (prompt, heading) => {
        const dialogService = await di.getAsync(TmDialogService);
        return dialogService.open(prompt, heading);
    };

    di.provide(AlarmClockService, new AlarmClockService());
    di.provide(DynamicComponentRegistry, new DynamicComponentRegistry(true));
    di.provide(OpenIdentityDialogToken as unknown as InjectionToken<unknown>, openIdentityDialog);
    di.provide(OpenIdleDialogToken as unknown as InjectionToken<unknown>, openIdleDialog);
    di.provide(
        OpenTimeAccessDialogToken as unknown as InjectionToken<unknown>,
        openTimeAccessDialog
    );
    di.provide(DrawerConfirmationPromptToken, openDialogForSideDrawer);
    di.provide(TmHttpClient, TmHttpClientMfa);
    di.provide(NavigationService, PwaNavigationService);
    di.provide(StatusService, ChannelStatusService);
    di.provide(TmDialogService, ModernDialogService);
    di.provideFactoryAsync(
        FeatureFlagService,
        [
            TmHttpClient,
            SessionStorageService,
            ConfigurationService,
            AuthenticationService,
            AccountService,
            LoggingService,
        ] as const,

        async (http, storage, config, auth, accountService, logger) => {
            await auth.authenticated$.toPromise();

            const { loginId, isAdmin, isSuperUser, companyId } =
                await accountService.getCurrentUser();
            const { institutionId } = config;
            const ffService = new FeatureFlagService(http, storage, config, logger);

            await ffService.init(institutionId, {
                userId: loginId,
                companyId,
                isAdmin,
                isSuperUser,
            });

            return ffService;
        },
        NullFeatureFlagService
    );
    di.provideFactoryAsync(
        FeatureVisibilityService,
        [
            FeatureFlagService,
            EntitlementsService,
            AuthenticationService,
            NavigationsService,
            DashboardService,
            RemoteDepositCaptureService,
            DeviceService,
        ] as const,
        async (ffService, entitlements, auth, navigations, dashboard, rdc, deviceService) => {
            await auth.authenticated$.toPromise();
            const fvService = new FeatureVisibilityService(
                ffService,
                entitlements,
                auth,
                navigations,
                dashboard,
                rdc,
                deviceService
            );

            await fvService.init(PwaVisibilityFeatureConditionMap);
            return fvService;
        }
    );

    di.provideFactory(LoggingService, [WindowService, ConfigurationService], (window, config) => {
        const logger = new LoggingService(config, window);
        logger.init();

        return logger;
    });

    di.provideFactoryAsync(
        AnalyticsService,
        [
            ConfigurationService,
            FeatureFlagService,
            AuthenticationService,
            AccountService,
            LoggingService,
        ] as const,
        async (config, ffService, auth, accountService, logger) => {
            await auth.authenticated$.toPromise();
            await ffService.ready$.toPromise();

            try {
                const analyticsEnabled = await ffService.isEnabled(Feature.Analytics);
                const analyticsService = analyticsEnabled
                    ? new AnalyticsService(config, logger, ffService)
                    : NullAnalyticsService;

                const user = await accountService.getCurrentUser();

                await analyticsService.init();
                analyticsService.identify(user);

                return analyticsService;
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('Failed to instantiate AnalyticsService');
                throw e;
            }
        },
        NullAnalyticsService
    );

    await DiContainer.init(di);

    // TODO: consolidate auth service usage
    const channelAuthService = di.get(ChannelAuthenticationService);
    const authService = di.get(AuthenticationService);
    const logoutManager = di.get(LogoutManager);
    const http = di.get(TmHttpClient);
    const navService = di.get(NavigationService);
    const configService = di.get(ConfigurationService);
    const entitlementsService = di.get(EntitlementsService);
    const statusService = di.get(StatusService);
    const offlineDialog = di.get(OfflineDialogService);
    const loggingService = di.get(LoggingService);
    const applicationAreaService = di.get(ApplicationAreaService);
    const accountService = di.get(AccountService);
    const footerService = di.get(TmFooterService);
    const themeService = di.get(ThemeService);
    const companyAccountService = di.get(CompanyAccountService);

    // set debug state out-of-band so logging service can be consumed synchronously
    di.getAsync(FeatureFlagService)
        .then(ffService =>
            Promise.all([di.getAsync(LoggingService), ffService.isEnabled(Feature.DebugMode)])
        )
        .then(([logger, isDebugEnabled]) => {
            logger.isDebugEnabled = isDebugEnabled;
        });

    themeService.removeStoredTheme();
    configureBranding(configService.institutionId);

    // hide footer on navigation so views without footers don't have to manually hide previous footer
    navService.routeChange$.subscribe(() => footerService.hide());

    channelAuthService.authEvent$.subscribe(event => {
        switch (event) {
            case AuthFlowEvent.TermsAndConditionsAccept:
                navService.navigate('/terms-and-conditions-accept');
                break;
            case AuthFlowEvent.Done:
                navService.navigate('/dashboard');
                break;
            default:
                break;
        }
    });

    statusService.online$.subscribe(async isOnline => {
        if (isOnline) {
            offlineDialog.close();
        } else {
            offlineDialog.open();
        }
    });

    authService.uisMetadataPromise.then(metadata => {
        cachedIsUisEnabled = metadata?.isUisEnabled ?? false;
    });

    logoutManager.logout$.subscribe(() => onSessionEnd(di));
    http.sessionExpired$.subscribe(() => onSessionEnd(di));
    http.error$.subscribe(err => {
        loggingService.logError('Fetch error', err);
    });
    navService.setOutlet(AppComponent.getOutlet);

    const alarmClockService = di.get(AlarmClockService);

    authService.authenticated$.subscribe(() =>
        onAuthenticated(
            companyAccountService,
            configService,
            http,
            entitlementsService,
            accountService,
            loggingService,
            logoutManager,
            applicationAreaService,
            alarmClockService,
            worker
        )
    );
}

async function onAuthenticated(
    companyAccountService: CompanyAccountService,
    config: ConfigurationService,
    http: TmHttpClient,
    entitlementsService: EntitlementsService,
    accountService: AccountService,
    loggingService: LoggingService,
    logoutManager: LogoutManager,
    aaService: ApplicationAreaService,
    alarmClockService: AlarmClockService,
    worker?: ServiceWorkerRegistration
) {
    if (worker) {
        const { apiRoot } = config;
        const { authToken } = http;
        worker.active?.postMessage({
            authToken,
            apiRoot,
        });
    }

    await entitlementsService.init();
    const user = await accountService.getCurrentUser();
    loggingService.setUserSessionContext(user);
    logoutManager.startLogoutTimers();
    aaService.init();
    companyAccountService.getCutoffTimes().then(cutoffs => {
        alarmClockService.init({
            fiTime: cutoffs.currentFiTime ?? '',
            timeZone: /(All (\w+))/.exec(cutoffs.timeZone ?? '')?.[2] ?? cutoffs.timeZone ?? '',
            alarms: buildAlarmConfigs(cutoffs.processingCutoffs ?? []),
        });
    });

    const di = await DiContainer.getInstance();
    di.getAsync(AnalyticsService).then(analytics => {
        analytics.track(AnalyticsEvent.LogIn);
    });

    const ffService = await di.getAsync(FeatureFlagService);
    initFfService(ffService, accountService, config.institutionId).then(async () => {
        const coBrowseEnabled = await ffService.isEnabled(Feature.CoBrowse);
        if (coBrowseEnabled) {
            const { CoBrowseService } = await import('@treasury/core/co-browse');
            const windowService = di.get(WindowService);
            const coBrowseService = new CoBrowseService(windowService);
            coBrowseService.init();
            di.provide(CoBrowseService, coBrowseService);
            coBrowseInitialized = true;
        }
    });
}

async function onSessionEnd(di: DiContainer) {
    const isUisEnabled = cachedIsUisEnabled;
    const { isDesktop } = di.get(DeviceService);

    const dialogService = di.get(TmDialogService);
    dialogService.closeAll();

    const applicationAreaService = di.get(ApplicationAreaService);
    applicationAreaService.reset();

    const httpClient = di.get(TmHttpClient);
    if (httpClient instanceof JhHttpClientCached) {
        httpClient.clearCache();
    }

    const entitlementsService = di.get(EntitlementsService);
    entitlementsService.reset();

    const ffService = await di.getAsync(FeatureFlagService);
    ffService.reset();

    try {
        const analytics = await di.getAsync(AnalyticsService);
        analytics.track(AnalyticsEvent.LogOut);
        analytics.reset();
    } catch {
        // no-op if analytics service isn't available
    }

    if (coBrowseInitialized) {
        const { CoBrowseService } = await import('@treasury/core/co-browse');
        const coBrowseService = di.get(CoBrowseService);
        coBrowseService.stop();
        coBrowseInitialized = false;
    }

    const navService = di.get(NavigationService);
    if (isDesktop && isUisEnabled) {
        navService.navigate('log-out/confirmed');
    } else {
        navService.navigate('login');
    }
}

async function initFfService(
    ffService: FeatureFlagService,
    accountService: AccountService,
    institutionId: string
) {
    const { loginId, isAdmin, isSuperUser, companyId } = await accountService.getCurrentUser();

    // Pass local feature flag overrides in dev builds via assets/feature-flags.local.json
    const localFlagsPath =
        process.env['NODE_ENV'] !== 'production' ? '/assets/feature-flags.local.json' : undefined;

    await ffService.init(
        institutionId,
        {
            userId: loginId,
            companyId,
            isAdmin,
            isSuperUser,
        },
        localFlagsPath
    );
}
